import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, SelectChangeEvent, Dialog, DialogContent, useMediaQuery, useTheme, TextField, InputAdornment, IconButton, DialogActions, Tooltip } from '@mui/material';
import { DataGrid, GridColDef, GridAlignment, GridRowSelectionModel } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import DevicesIcon from '@mui/icons-material/Devices';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import imageCompression from 'browser-image-compression';
import Dropdown from '../../Components/DropDown/drop-down.tsx';
import Heading from '../../Components/Heading/heading.tsx';
import StatusFilterTabs from '../../Components/Tab/tab.tsx';
import NavBar from '../../Components/Navbar/sidenav.tsx';
import Header from '../../Components/Header/header.tsx';
import ConfirmationPopup from '../../Components/ConfirmationModal/confirmation-modal.tsx';
import Newpagination from '../../Components/Pagination/pagination.tsx';
import AddNewModelModal from './addNewModelModal.tsx';
import PreviewFileModal from './previewFileModal.tsx';
import AddDeviceModal from './addDevicesModal.tsx';
import AddSimModal from './addSimModal.tsx';
import AddServicesModal from './addServicesModal.tsx';
import AddDeploymentModal from './addDeploymentModal.tsx';
import '../../Components/Button/button-style.scss';

import { 
    getServices, addServices, getDevicesSim, bulkUploadDevices, editServices,
    getVehicle, getCompanies, addDeployment, getModels, editDevicesSim, editVehicle,
    searchDevicesSim, getAssignedUsers, addPhotos, addDeviceSimPairing
} from './action.ts'; 

const DEVICE_TRANSITIONS: Record<string, string[]> = {
    'unallocated': ['faulty', 'to be deployed', 'scrap'],
    'to be deployed': ['deployed'],
    'deployed': ['faulty', 'scrap'],
    'faulty': ['scrap'],
    'scrap': []
};

const SIM_TRANSITIONS: Record<string, string[]> = {
    'inactive': ['active', 'returned'],
    'active': ['faulty', 'to be deployed', 'returned'],
    'to be deployed': ['deployed'],
    'deployed': ['faulty', 'returned'],
    'faulty': ['active','returned'],
    'returned': []
};

const DEPLOYMENT_TRANSITIONS: Record<string, string[]> = {
    'sim insertion': ['faulty', 'to be deployed'],
    'to be deployed': ['deployed'],
    'deployed': ['faulty','returned'],
    'faulty': []
};

const getDropdownOptions = (currentStatus: string, transitionsMap: Record<string, string[]>) => {
    const currentLower = currentStatus.toLowerCase();
    const allowedNextStates = transitionsMap[currentLower] || [];
    const allStates = Array.from(new Set([currentLower, ...allowedNextStates]));
    return allStates.map(state => {
        let label = state.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
        if (state === 'sim') label = 'SIM';
        if (state === 'sim insertion') label = 'SIM Insertion';
        
        return { id: state, label };
    });
};

const DEVICE_HEADER_STATUS_OPTIONS = [
    { id: 'All', label: 'All' },
    { id: 'unallocated', label: 'Unallocated' },
    { id: 'to be deployed', label: 'To be Deployed' },
    { id: 'faulty', label: 'Faulty' },
    { id: 'deployed', label: 'Deployed' },
    { id: 'scrap', label: 'Scrap' }
];

const SIM_HEADER_STATUS_OPTIONS = [
    { id: 'All', label: 'All' },
    { id: 'to be deployed', label: 'To be Deployed' },
    { id: 'active', label: 'Active' },
    { id: 'inactive', label: 'Inactive' },
    { id: 'faulty', label: 'Faulty' },
    { id: 'returned', label: 'Returned' },
    { id: 'deployed', label: 'Deployed' }
];

const DEPLOYMENT_HEADER_STATUS_OPTIONS = [
    { id: 'All', label: 'All' },
    { id: 'sim insertion', label: 'SIM Insertion' },
    { id: 'to be deployed', label: 'To be Deployed' },
    { id: 'deployed', label: 'Deployed' },
];

const Devices: React.FC = () => {
    const centerAlign: GridAlignment = 'center';
    const centerCol = {
      headerAlign: centerAlign,
      align: centerAlign,
    };    
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [activeTab, setActiveTab] = useState<string>(() => {
        return localStorage.getItem('activeDeviceTab') || 'Devices';
    });
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [page, setPage] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(10);
    const [statusFilter, setStatusFilter] = useState<string>('All');
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [appliedSearchQuery, setAppliedSearchQuery] = useState<string>(''); 
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
    const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
    const [isAddModelModalOpen, setIsAddModelModalOpen] = useState<boolean>(false);
    const [isAddPlatformModalOpen, setIsAddPlatformModalOpen] = useState<boolean>(false);
    const [assignedUserOptions, setAssignedUserOptions] = useState<{id: string, label: string}[]>([]);
    const [selectedAssignedUser, setSelectedAssignedUser] = useState<string>('');
    const [isFaultyModalOpen, setIsFaultyModalOpen] = useState<boolean>(false);
    const [faultyComponentSelection, setFaultyComponentSelection] = useState<string>('');
    const [faultyRowData, setFaultyRowData] = useState<any>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [serviceProvider, setServiceProvider] = useState<string>('');
    const [deviceType, setDeviceType] = useState<string>('');
    const [serviceType, setServiceType] = useState<string>('');
    const [serviceModel, setServiceModel] = useState<string>('');
    const [platformModel, setPlatformModel] = useState<string>('');
    const [sellingPrice, setSellingPrice] = useState<string>('');
    const [remarks, setRemarks] = useState<string>('');
    const [isPhotoModalOpen, setIsPhotoModalOpen] = useState<boolean>(false);
    const [vehicleNumberPlatePic, setVehicleNumberPlatePic] = useState<File | null>(null);
    const [deployedDevicePic, setDeployedDevicePic] = useState<File | null>(null);

    const [isStatusConfirmOpen, setIsStatusConfirmOpen] = useState<boolean>(false);
    const [pendingStatusChange, setPendingStatusChange] = useState<{
        row?: any, 
        newStatus: string, 
        tab: string, 
        isBulk?: boolean, 
        selectedIds?: any[] 
    } | null>(null);    
    const [selectedCompany, setSelectedCompany] = useState<string>('ALL');
    const [modalSelectedCompany, setModalSelectedCompany] = useState<string>('');
    const [deploymentDevice, setDeploymentDevice] = useState<string>('');
    const [selectedVehicle, setSelectedVehicle] = useState<string>('');
    const [newModelName, setNewModelName] = useState<string>('');
    const [newPlatformName, setNewPlatformName] = useState<string>('');
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [selectedServiceId, setSelectedServiceId] = useState<number | string | null>(null);
    const [deviceTypeOptions, setDeviceTypeOptions] = useState<{id: string, label: string}[]>([]);
    const [companyOptions, setCompanyOptions] = useState<{id: string, label: string}[]>([]);
    const [vehicleOptions, setVehicleOptions] = useState<{id: string, label: string}[]>([]);
    const [serviceModelOptions, setServiceModelOptions] = useState<{id: string, label: string}[]>([{ id: 'ADD_NEW_MODEL', label: '+ Add New Model' }]);
    const [platformModelOptions, setPlatformModelOptions] = useState<{id: string, label: string}[]>([{ id: 'ADD_NEW_MODEL', label: '+ Add New Model' }]);
    const [allModelsData, setAllModelsData] = useState<{devices: any[], subscriptions: any[]}>({ devices: [], subscriptions: [] });
    const [rows, setRows] = useState<any[]>([]);
    const [columns, setColumns] = useState<GridColDef[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [previewData, setPreviewData] = useState<any[]>([]);
    const [previewColumns, setPreviewColumns] = useState<GridColDef[]>([]);
    const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
    const showEntries = [{ id: '10', label: '10' }, { id: '20', label: '20' }, { id: '50', label: '50' }, { id: '100', label: '100' }];
    const serviceTypeOptions = [{ id: 'device', label: 'Device Type' }, { id: 'subscription', label: 'Subscription' }];
    const serviceProviderOptions = [{ id: 'airtel', label: 'Airtel' }, { id: 'bsnl', label: 'BSNL' }, { id: 'jio', label: 'Jio' }];
    const userDetailsFromStorage = JSON.parse(localStorage.getItem("userdetails") || "null");

    useEffect(() => {
        if (isMobile && activeTab !== 'Deployment') {
            setActiveTab('Deployment');
            setPage(0);
            setStatusFilter('All');
        }
    }, [isMobile]);

    useEffect(() => {
        localStorage.setItem('activeDeviceTab', activeTab);
    }, [activeTab]);

    const fetchAssignedUsers = async (companyId: string) => {
        try {
            getAssignedUsers(
                "",
                "",
                "platform",
                "",
                '', 
                100,
                0,
                (response: any) => {
                    const usersArray = response?.message?.user || [];
                    const users = usersArray.map((user: any) => ({
                        id: user.user_unique_id?.toString(),
                        label: user.user_name || 'Unknown User'
                    }));
                    
                    setAssignedUserOptions(users);
                },
                (errorData: any) => {
                    console.error("Failed to fetch assigned users", errorData);
                    setAssignedUserOptions([]);
                }
            );
        } catch (error) {
            setAssignedUserOptions([]);
            console.error("Failed to fetch assigned users", error);
        }
    };

    useEffect(() => {
        if (activeTab === 'Services') {
            fetchServicesData(true);
        } else if (activeTab === 'Devices' || activeTab === 'SIM') {
            fetchDevicesSimData();
        } else if (activeTab === 'Deployment') {
            fetchDeploymentData();
        } else {
            setRows([]);
            setColumns([]);
        }
    }, [activeTab, page, pageSize, isMobile, statusFilter, appliedSearchQuery]); 

    useEffect(() => {
        if (activeTab === 'Deployment') {
            fetchCompaniesData();
        } 
    }, [activeTab]); 

    useEffect(() => {
        if (typeof selectedCompany === 'string' && activeTab === 'Deployment') { 
            fetchDeploymentData();
        }
    }, [selectedCompany]);

    useEffect(() => {
        if (!serviceType) {
            setServiceModelOptions([{ id: 'ADD_NEW_MODEL', label: '+ Add New Model' }]);
            setPlatformModelOptions([{ id: 'ADD_NEW_MODEL', label: '+ Add New Model' }]);
            return;
        }

        const dataToUse = serviceType === 'device' ? allModelsData.devices : allModelsData.subscriptions;
        const uniqueModels = Array.from(new Set(dataToUse.map(item => item.model))).filter(Boolean);
        const mappedModels = uniqueModels.map(m => ({ id: m as string, label: m as string }));
        const uniquePlatformModels = Array.from(new Set(dataToUse.map(item => item.platform_model))).filter(Boolean);
        const mappedPlatformModels = uniquePlatformModels.map(pm => ({ id: pm as string, label: pm as string }));
        setServiceModelOptions([...mappedModels, { id: 'ADD_NEW_MODEL', label: '+ Add New Model' }]);
        setPlatformModelOptions([...mappedPlatformModels, { id: 'ADD_NEW_MODEL', label: '+ Add New Model' }]);
    }, [serviceType, allModelsData]);

    const handleSearchClick = () => {
        setAppliedSearchQuery(searchQuery);
        setPage(0);
    };

    const fetchModelsList = () => {
        getModels(
            (response: any) => {
                if (response?.data) {
                    setAllModelsData({
                        devices: response.data.devices || [],
                        subscriptions: response.data.subscriptions || []
                    });
                }
            },
            (error: any) => {
                console.error("Failed to fetch models", error);
            }
        );
    };

    const handlePageClick = (event: React.ChangeEvent<unknown>, newPage: number) => { setPage(newPage - 1); };

    const handleStatusFilterChange = (event: SelectChangeEvent<string>) => {
        setStatusFilter(event.target.value);
        setPage(0);
        setSelectedRows([]);
    };

    const initiateStatusUpdate = (row: any, newStatus: string, tab: string) => {
        if (tab === 'Deployment' && newStatus.toLowerCase() === 'faulty') {
            setFaultyRowData(row);
            setFaultyComponentSelection(''); 
            setIsFaultyModalOpen(true);
            return; 
        }

        setPendingStatusChange({ row, newStatus, tab });
        if (tab === 'Deployment' && newStatus.toLowerCase() === 'deployed') {
            setIsPhotoModalOpen(true);
        } else {
            setIsStatusConfirmOpen(true);
        }
    };

    const initiateBulkStatusUpdate = (event: SelectChangeEvent<string>) => {
        const newStatus = event.target.value;
        if (newStatus === 'default') return;

        setPendingStatusChange({ 
            newStatus, 
            tab: 'SIM', 
            isBulk: true, 
            selectedIds: [...selectedRows] 
        });
        setIsStatusConfirmOpen(true);
    };

    const handleClosePhotoModal = () => {
        setIsPhotoModalOpen(false);
        setVehicleNumberPlatePic(null);
        setDeployedDevicePic(null);
        setPendingStatusChange(null);
    };

    const handleImageSelection = async (
        file: File | undefined, 
        setFileState: React.Dispatch<React.SetStateAction<File | null>>
    ) => {
        if (!file) {
            setFileState(null);
            return;
        }

        const fileSizeMB = file.size / 1024 / 1024;
        
        if (fileSizeMB > 2) {
            const options = {
                maxSizeMB: 2,
                maxWidthOrHeight: 1920,
                useWebWorker: true,
                fileType: file.type
            };
            
            try {
                const compressedBlob = await imageCompression(file, options);
                const compressedFile = new File([compressedBlob], file.name, {
                    type: file.type,
                    lastModified: Date.now(),
                });
                
                setFileState(compressedFile);
            } catch (error) {
                console.error("Error compressing image:", error);
                toast.error("Failed to compress image before upload.");
                setFileState(null);
            }
        } else {
            setFileState(file);
        }
    };

    const handlePhotoUploadAndDeploy = () => {
        if (!vehicleNumberPlatePic || !deployedDevicePic) {
            toast.error("Both vehicle number plate and deployed device photos are required.");
            return;
        }

        const row = pendingStatusChange?.row;
        if (!row || !row.number_plate) {
            toast.error("Vehicle data is missing.");
            return;
        }

        addPhotos(
            row.number_plate,
            vehicleNumberPlatePic,
            deployedDevicePic,
            (response) => {
                toast.success("Photos uploaded successfully.");
                setIsPhotoModalOpen(false);
                setVehicleNumberPlatePic(null);
                setDeployedDevicePic(null);
                executeStatusUpdate();
            },
            (errorMsg) => {
                console.error("Failed to upload photos:", errorMsg);
                toast.error(errorMsg?.message || "Failed to upload photos.");
            }
        );
    };

    const handleFaultyComponentSubmit = () => {
        if (!faultyComponentSelection) {
            toast.error("Please select a component (SIM or Device)");
            return;
        }

        const row = faultyRowData;
        const simId = row?.sim_id || row?.sim?.id || '';
        const deviceId = row?.device_id || row?.device?.id || '';

        const payload = {
            type: faultyComponentSelection,
            sim_id: faultyComponentSelection === 'sim' ? simId : '',
            sim_status: faultyComponentSelection === 'sim' ? 'faulty' : '',
            device_id: faultyComponentSelection === 'device' ? deviceId : '',
            device_status: faultyComponentSelection === 'device' ? 'faulty' : ''
        };

        editDevicesSim(
            faultyComponentSelection,
            payload,
            (response: any) => {
                toast.success(response?.message || `${faultyComponentSelection === 'sim' ? 'SIM' : 'Device'} marked as faulty successfully`);
                setIsFaultyModalOpen(false);
                fetchDeploymentData(); 
            },
            (errorMsg: any) => {
                console.error(`Failed to update ${faultyComponentSelection} status:`, errorMsg);
                toast.error(errorMsg?.message || `Failed to update ${faultyComponentSelection} status`);
            }
        );
    };

    const executeStatusUpdate = () => {
        if (!pendingStatusChange) return;

        const { row, newStatus, tab, isBulk, selectedIds } = pendingStatusChange;
        if (isBulk && selectedIds) {
            setRows((prevRows) => 
                prevRows.map((r) => {
                    if (selectedIds.includes(r.id)) {
                        return { ...r, sim_status: newStatus };
                    }
                    return r;
                })
            );

            const payload: any = {
                type: 'sim',
                sim_ids: selectedIds, 
                sim_status: newStatus
            };

            editDevicesSim(
                'sim',
                payload,
                (response) => {
                    toast.success(`Selected SIMs updated to ${newStatus} successfully`);
                    setSelectedRows([]); 
                    fetchDevicesSimData(); 
                },
                (errorMsg) => {
                    console.error(`Failed to bulk update SIM status:`, errorMsg);
                    toast.error(errorMsg?.message || `Failed to bulk update SIM status`);
                    fetchDevicesSimData(); 
                }
            );
        } 
        else if (row) {
            const safeId = row.id || row.sim_id || row.device_id; 

            setRows((prevRows) => 
                prevRows.map((r) => {
                    if (r.id === safeId) {
                        if (tab === 'Devices') return { ...r, status: newStatus };
                        if (tab === 'SIM') return { ...r, sim_status: newStatus };
                        if (tab === 'Deployment') return { ...r, device_status: newStatus };
                    }
                    return r;
                })
            );

            if (tab === 'Devices' || tab === 'SIM') {
                const payload: any = {
                    type: tab === 'Devices' ? 'device' : 'sim'
                };

                if (tab === 'SIM') {
                    payload.sim_id = safeId;
                    payload.sim_status = newStatus;
                } else if (tab === 'Devices') {
                    payload.device_id = safeId;
                    payload.device_status = newStatus;
                }

                const apiType = tab === 'Devices' ? 'device' : 'sim';

                editDevicesSim(
                    apiType,
                    payload,
                    (response) => {
                        toast.success(`${tab} status updated successfully`);
                    },
                    (errorMsg) => {
                        console.error(`Failed to update ${tab} status:`, errorMsg);
                        toast.error(errorMsg?.message || `Failed to update ${tab} status`);
                        fetchDevicesSimData(); 
                    }
                );
            }
            
            if (tab === 'Deployment') {
                const numberPlate = row.number_plate || '';
                const today = new Date();
                const year = today.getFullYear();
                const month = String(today.getMonth() + 1).padStart(2, '0'); 
                const day = String(today.getDate()).padStart(2, '0');
                const deploymentDate = `${year}-${month}-${day}`;
                const deployedUsersId = userDetailsFromStorage?.user?.id || '';

                editVehicle(
                    numberPlate,
                    deploymentDate,
                    newStatus,
                    deployedUsersId,
                    row.companies_id|| '',
                    (response) => {
                        toast.success(`Deployment status updated successfully`);
                    },
                    (errorMsg) => {
                        console.error(`Failed to update deployment status:`, errorMsg);
                        toast.error(errorMsg?.message || `Failed to update deployment status`);
                        fetchDeploymentData();
                    }
                );
            }
        }

        setIsStatusConfirmOpen(false);
        setPendingStatusChange(null);
    };

    const handleDownloadFile = (url: string, defaultFilename: string) => {
        const fullUrl = url.startsWith('http') ? url : `/${url}`; 
        
        const link = document.createElement('a');
        link.href = fullUrl;
        link.target = '_blank';
        link.download = defaultFilename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const fetchDeploymentData = async () => {
        setLoading(true);
        const apiCompanyId = selectedCompany === 'ALL' ? '' : selectedCompany;
        try {
            getVehicle(
                1, 
                pageSize,
                page,
                (response: any) => {
                    const vehicleData = response?.message?.vehicles_list || [];
                    const mappedVehicles = vehicleData.map((vehicle: any, index: number) => ({
                        ...vehicle,
                        id: vehicle.id || index + 1, 
                        sl_no: (page * pageSize) + index + 1
                    }));
                    setRows(mappedVehicles);

                    const statusColumn: GridColDef = {
                        field: 'status',
                        headerName: 'Device Status',
                        flex: 1.5,
                        minWidth: isMobile ? 150 : 170,
                        ...centerCol,
                        renderCell: (params) => {
                            const rawStatus = params.row.device_status || 'sim insertion';
                            const currentVal = rawStatus.toLowerCase();
                            
                            const options = getDropdownOptions(currentVal, DEPLOYMENT_TRANSITIONS);

                            return (
                                <Dropdown
                                    options={options}
                                    selectedValue={currentVal}
                                    handleChange={(event: SelectChangeEvent<string>) =>
                                        initiateStatusUpdate(params.row, event.target.value, 'Deployment')
                                    }
                                    width="160px"
                                    height="35px"
                                />
                            );
                        }
                    };

                    const deploymentColumns: GridColDef[] = [
                        ...(isMobile ? [] : [{
                            field: 'sl_no',
                            headerName: 'Sl.No',
                            width: 80,
                            ...centerCol
                        }]),
                        {
                            field: 'number_plate',
                            headerName: 'Vehicle Number',
                            flex: 1,
                            minWidth: 120,
                            ...centerCol
                        },
                        {
                            field: 'device_imei',
                            headerName: 'Device IMEI',
                            flex: 1,
                            minWidth: 140,
                            ...centerCol,
                            renderCell: (params) =>
                                params.row.device_imei || params.row.device?.device_imei || '-'
                        },
                        
                        ...(isMobile ? [statusColumn] : []),

                        {
                            field: 'sim_number',
                            headerName: 'SIM Number',
                            flex: 1,
                            minWidth: 130,
                            ...centerCol,
                            renderCell: (params) =>
                                params.row.sim_number || params.row.sim?.sim_number || '-'
                        },

                        ...(isMobile ? [] : [
                            {field: 'deployment_assigned_users_name', headerName: 'Assigned User', flex: 1, ...centerCol },
                            {field: 'deployed_users_name', headerName: 'Deployed By', flex: 1, ...centerCol },
                            { field: 'driver_name', headerName: 'Driver Name', flex: 1, ...centerCol },
                            { field: 'driver_phone', headerName: 'Driver Phone', flex: 1, ...centerCol },
                            {
                                field: 'action', 
                                headerName: 'Action', 
                                flex: 1.5, 
                                ...centerCol, 
                                renderCell: (params) => (
                                    <Box display="flex" gap={1} justifyContent="center" alignItems="center">
                                    
                                        
                                        {params.row.vehicle_number_plate_url && (
                                            <Tooltip title="Download Vehicle Photo">
                                                <IconButton 
                                                    color="primary" 
                                                    size="small"
                                                    onClick={() => handleDownloadFile(`${global.company_documents_image_url}${params.row.vehicle_number_plate_url}`, 'vehicle_number_plate.jpg')}
                                                >
                                                    <DirectionsCarIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}

                                        {params.row.deployed_device_url && (
                                            <Tooltip title="Download Deployed Device Photo">
                                                <IconButton 
                                                    color="primary" 
                                                    size="small"
                                                    onClick={() => handleDownloadFile(`${global.company_documents_image_url}${params.row.deployed_device_url}`, 'deployed_device.jpg')}
                                                >
                                                    <DevicesIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                )},
                            statusColumn
                        ]),
                    ];

                    setColumns(deploymentColumns);
                    setTotalRecords(response?.message?.total_vehicles_count || vehicleData.length);
                    setLoading(false);
                },
                (errorData: any) => {
                    if (errorData?.status === 404) {
                        setRows([]); 
                        setTotalRecords(0);
                    }
                    console.error("Failed to fetch deployment/vehicle data", errorData);
                    setLoading(false);
                },
                apiCompanyId,
                statusFilter === 'All' ? '' : statusFilter
            );
        } catch (error) {
            setRows([]);
            setTotalRecords(0);
            console.error("Failed to fetch deployment/vehicle data", error);
            setLoading(false);
        }
    };

    const fetchCompaniesData = async () => {
        try {
            getCompanies(
                100, 
                0,
                null,
                (response: any) => {
                    const companiesData = response?.message || [];
                    const options = companiesData.map((item: any) => ({
                        id: item.company.id.toString(),
                        label: item.company.name 
                    }));
                    options.sort((a: any, b: any) => a.label.localeCompare(b.label));

                    setCompanyOptions(options);

                    setSelectedCompany((prevSelected) => {
                        const savedCompanyId = localStorage.getItem('selectedDeploymentCompany');
                        const allOptions = [{ id: 'ALL', label: 'All' }, ...options];
                        
                        if (savedCompanyId !== null) {
                            const savedExists = allOptions.some((opt: any) => opt.id === savedCompanyId);
                            if (savedExists) return savedCompanyId;
                        }

                        const exists = allOptions.some((opt: any) => opt.id === prevSelected);
                        if (!exists) {
                            return 'ALL';
                        }
                        return prevSelected;
                    });
                },
                (error: any) => {
                    console.error("Failed to fetch companies", error);
                }
            );
        } catch (error) {
            console.error("Failed to fetch companies", error);
        }
    };

    const fetchAvailableVehicles = async (companyId: string) => {
        try {
            getVehicle(
                0, 
                100, 
                0,
                (response: any) => {
                    const vehicleData = response?.message?.vehicles_list || [];
                    const options = vehicleData.map((v: any) => ({
                        id: v.id.toString(),
                        label: v.number_plate 
                    }));
                    setVehicleOptions(options);
                },
                (errorData: any) => {
                    if (errorData?.status === 404) {
                        setVehicleOptions([]);
                    }
                    console.error("Failed to fetch available vehicles", errorData);
                },
                companyId
            );
        } catch (error) {
            setVehicleOptions([]);
            console.error("Failed to fetch available vehicles", error);
        }
    };

    const fetchDevicesSimData = async () => {
        setLoading(true);
        const list_devices = activeTab === 'Devices' ? 1 : undefined;
        const list_sims = activeTab === 'SIM' ? 1 : undefined;
        const statusParam = statusFilter === 'All' ? '' : statusFilter;

        const processResponse = (response: any) => {
            if (activeTab === 'Devices') {
                const devicesData = response?.data?.devices || response?.devices || [];
                const mappedDevices = devicesData.map((device: any, index: number) => ({
                    ...device,
                    id: device.device_id,
                    sl_no: (page * pageSize) + index + 1
                }));

                setRows(mappedDevices);
                setColumns([
                    { field: 'sl_no', headerName: 'Sl.No', width: 50 },
                    { field: 'device_imei', headerName: 'IMEI Number', flex: 1 },
                    { field: 'service_model', headerName: 'Service', flex: 1 , headerAlign: 'center', align: 'center'},
                    { 
                        field: 'sim_number', 
                        headerName: 'SIM', 
                        flex: 1,
                        renderCell: (params) => params.row.sim_number ? params.row.sim_number : '-' 
                    },
                    { 
                        field: 'vehicle_number', 
                        headerName: 'Vehicle Number', 
                        flex: 1,
                        headerAlign: 'center',
                        align: 'center',
                        renderCell: (params) => params.row.vehicle?.number || '-' 
                    },
                    { 
                        field: 'vehicle_driver', 
                        headerName: 'Driver', 
                        flex: 1,
                            headerAlign: 'center',  
                            align: 'center',
                        renderCell: (params) => params.row.vehicle?.driver || '-' 
                    },
                    { 
                        field: 'vehicle_company', 
                        headerName: 'Company', 
                        flex: 1,
                        headerAlign: 'center',
                        align: 'center',
                        renderCell: (params) => params.row.vehicle?.company || '-' 
                    },
                    { 
    field: 'status', 
    headerName: 'Status', 
    flex: 1.5,
    renderCell: (params) => {
        const rawStatus = params.row.status || 'unallocated';
        const currentVal = rawStatus.toLowerCase();
        
        const isToBeDeployed = currentVal === 'to be deployed';
        const options = isToBeDeployed 
            ? [{ id: 'to be deployed', label: 'To be Deployed' }]
            : getDropdownOptions(currentVal, DEVICE_TRANSITIONS);

        return (
            <Dropdown
                options={options}
                selectedValue={currentVal}
                handleChange={(event: SelectChangeEvent<string>) => {
                    if (!isToBeDeployed) {
                        initiateStatusUpdate(params.row, event.target.value, 'Devices');
                    }
                }}
                width="160px"
                height="35px"
                disabled={isToBeDeployed}
            />
        );
    }
}
                    
                ]);
                setTotalRecords(response?.total_count || devicesData.length);
            } else if (activeTab === 'SIM') {
                const simsData = response?.data?.sims || response?.data || response?.sims || [];
                const mappedSims = simsData.map((sim: any, index: number) => ({
                    ...sim,
                    id: sim.sim_id,
                    sl_no: (page * pageSize) + index + 1
                }));
                setRows(mappedSims);
                setColumns([
                    { field: 'sl_no', headerName: 'Sl.No', width: 90, headerAlign: 'center', align: 'center' },
                    { field: 'sim_number', headerName: 'SIM Number', flex: 1, headerAlign: 'center', align: 'center' },
                    { field: 'service_provider', headerName: 'Service Provider', flex: 1, headerAlign: 'center', align: 'center' },
                            { 
                    field: 'device_imei', 
                    headerName: 'Device IMEI', 
                    flex: 1, 
                    headerAlign: 'center', 
                    align: 'center',
                    renderCell: (params) => params.row.device?.device_imei || '-'
                },
                { 
                    field: 'vehicle_number', 
                    headerName: 'Vehicle Number', 
                    flex: 1, 
                    headerAlign: 'center', 
                    align: 'center',
                    renderCell: (params) => params.row.vehicle?.vehicle_number || '-'
                },
                 { 
    field: 'sim_status', 
    headerName: 'SIM Status', 
    flex: 1.5,
    headerAlign: 'center',
    align: 'center',
    renderCell: (params) => {
        const rawStatus = params.row.sim_status || 'inactive';
        const currentVal = rawStatus.toLowerCase();
        
        const isToBeDeployed = currentVal === 'to be deployed';
        const options = isToBeDeployed 
            ? [{ id: 'to be deployed', label: 'To be Deployed' }]
            : getDropdownOptions(currentVal, SIM_TRANSITIONS);

        return (
            <Dropdown
                options={options}
                selectedValue={currentVal}
                handleChange={(event: SelectChangeEvent<string>) => {
                    if (!isToBeDeployed) {
                        initiateStatusUpdate(params.row, event.target.value, 'SIM');
                    }
                }}
                width="160px"
                height="35px"
                disabled={isToBeDeployed}
            />
        );
    }
}
                ]);
                setTotalRecords(response?.total_count || simsData.length);
            }
            setLoading(false);
        };

        try {
            if (appliedSearchQuery.trim() !== '') {
                const deviceSearch = activeTab === 'Devices' ? appliedSearchQuery : '';
                const simSearch = activeTab === 'SIM' ? appliedSearchQuery : '';
                
                searchDevicesSim(
                    deviceSearch,
                    simSearch,
                    list_devices,
                    list_sims,
                    processResponse,
                    (error: any) => {
                        console.error(`Failed to search ${activeTab} data`, error);
                        setRows([]);
                        setTotalRecords(0);
                        setLoading(false);
                    }
                );
            } else {
                getDevicesSim(
                    '',
                    statusParam,
                    list_devices,
                    list_sims,
                    pageSize,
                    page,
                    processResponse,
                    (error: any) => {
                        console.error(`Failed to fetch ${activeTab} data`, error);
                        setLoading(false);
                    }
                );
            }
        } catch (error) {
            console.error(`Failed to fetch ${activeTab} data`, error);
            setLoading(false);
        }
    };

    const fetchServicesData = async (updateGrid: boolean = true) => {
        if (updateGrid) setLoading(true);
        try {
            getServices(
                (response: any) => {
                    if (response && response.status === 200) {
                        const serviceData = response.data;
                        const deviceOptions = serviceData
                            .filter((item: any) => item.type?.toLowerCase() === 'device')
                            .map((item: any) => ({
                                id: item.id.toString(), 
                                label: item.model 
                            }));
                        
                        const platformOptions = serviceData
                            .filter((item: any) => item.type?.toLowerCase() === 'device')
                            .map((item: any) => ({
                                id: item.id.toString(), 
                                label: item.platform_model
                            }));                      
                        
                        setDeviceTypeOptions(deviceOptions);
                        setPlatformModelOptions(platformOptions);

                        if (updateGrid) {
                            const mappedServices = serviceData.map((service: any, index: number) => ({
                                ...service,
                                sl_no: (page * pageSize) + index + 1
                            }));
                            setRows(mappedServices);
                            setColumns([
                                { field: 'sl_no', headerName: 'Sl.No', width: 90 },
                                { field: 'model', headerName: 'Model', flex: 1 },
                                { field: 'type', headerName: 'Type', flex: 1 },
                                { field: 'platform_model', headerName: 'AnyTrakk Model', flex: 1 },
                                { field: 'selling_price', headerName: 'Selling Price', flex: 1 },
                                { 
                                    field: 'actions', 
                                    headerName: 'Actions', 
                                    width: 80,
                                    sortable: false,
                                    disableColumnMenu: true,
                                    renderCell: (params) => (
                                        <IconButton color="primary" onClick={() => handleEditServiceClick(params.row)}>
                                            <EditIcon />
                                        </IconButton>
                                    )
                                }
                            ]);
                            setTotalRecords(serviceData.length); 
                        }
                    }
                },
                (error: any) => {
                    console.error("Failed to fetch services", error);
                }
            );
        } catch (error) {
            console.error("Failed to fetch services", error);
        } finally {
            if (updateGrid) setLoading(false);
        }
    };

    const handleTabChange = (newTab: string) => {
        setActiveTab(newTab);
        setPage(0);
        setStatusFilter('All');
        setSearchQuery(''); 
        setAppliedSearchQuery(''); 
        resetForm();
        setSelectedRows([]);
    };

    const resetForm = () => {
        setDeviceType(''); 
        setServiceType('');
        setServiceModel('');
        setPlatformModel('');
        setSellingPrice('');
        setSelectedFile(null);
        setServiceProvider('');
        setPreviewData([]); 
        setPreviewColumns([]); 
        setRemarks('');
        setDeploymentDevice('');
        setSelectedVehicle('');
        setSelectedAssignedUser('');
        setIsEditMode(false);
        setSelectedServiceId(null);
    };

    const updateField = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        if (!isNaN(Number(value))) {
            setPageSize(Number(value));
            setPage(0);
        }
    };

    const handleEditServiceClick = (row: any) => {
        setIsEditMode(true);
        setSelectedServiceId(row.id);
        setServiceType(row.type?.toLowerCase());
        setServiceModel(row.model);
        setPlatformModel(row.platform_model || '');
        setSellingPrice(row.selling_price?.toString() || '');
        fetchModelsList();
        setIsModalOpen(true);
    };

    const handleOpenModal = () => {
        if (activeTab === 'Devices') {
            fetchServicesData(false); 
        } else if (activeTab === 'Deployment') {
            fetchServicesData(false);
            fetchCompaniesData();
        } else if (activeTab === 'Services') {
            fetchModelsList(); 
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        resetForm();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            const uploadType = activeTab === 'Devices' ? 'device' : 'sim';

            if (uploadType === 'device' && !deviceType) {
                alert("Please select a device type before selecting a file.");
                e.target.value = ''; 
                return;
            }
            if (uploadType === 'sim' && !serviceProvider) {
                alert("Please enter a service provider before selecting a file.");
                e.target.value = ''; 
                return;
            }

            const reader = new FileReader();
            reader.onload = (event) => {
                try {
                    const data = event.target?.result;
                    const workbook = XLSX.read(data, { type: 'binary' });
                    const sheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[sheetName];
                    let jsonData = XLSX.utils.sheet_to_json(worksheet);

                    if (jsonData.length > 0) {
                        jsonData = jsonData.map((row: any) => {
                            if (row.sum_id !== undefined && row.sum_id !== null) {
                                row.sum_id = parseInt(row.sum_id, 10);
                            }
                            if (row.sim_id !== undefined && row.sim_id !== null) {
                                row.sim_id = parseInt(row.sim_id, 10);
                            }
                            return row;
                        });

                        const newWorksheet = XLSX.utils.json_to_sheet(jsonData);
                        const newWorkbook = XLSX.utils.book_new();
                        XLSX.utils.book_append_sheet(newWorkbook, newWorksheet, sheetName);
                        const excelBuffer = XLSX.write(newWorkbook, { bookType: 'xlsx', type: 'array' });
                        const newFile = new File([excelBuffer], file.name, { 
                            type: file.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
                        });

                        setSelectedFile(newFile);

                        const keys = Object.keys(jsonData[0] as object);
                        const dynamicColumns: GridColDef[] = keys.map((key) => ({
                            field: key,
                            headerName: key.toUpperCase(),
                            flex: 1,
                            minWidth: 130
                        }));

                        const rowsWithIds = jsonData.map((row: any, index: number) => ({
                            ...row,
                            id: row.id || index + 1
                        }));

                        setPreviewColumns(dynamicColumns);
                        setPreviewData(rowsWithIds);
                        setIsPreviewOpen(true); 
                    } else {
                        console.error("The uploaded file is empty.");
                        alert("The uploaded file is empty.");
                    }
                } catch (error) {
                    console.error("Error reading file:", error);
                    alert("Error parsing the file.");
                }
            };
            reader.readAsBinaryString(file);
        }
        
        e.target.value = '';
    };


    const confirmAndUpload = () => {
        const uploadType = activeTab === 'Devices' ? 'device' : 'sim';

        bulkUploadDevices(
            uploadType,
            selectedFile!,
            uploadType === 'device' ? deviceType : null,
            uploadType === 'sim' ? serviceProvider : null,
            (response) => {
                const hasInsertedData = response?.records_inserted > 0;
                const statsString = hasInsertedData 
  ? `, Accepted: ${response.records_inserted}, Rejected: ${response?.records_skipped_duplicates || 0}` 
  : "";

                toast.success(`${response?.message || "Success"}${statsString}`);
                setIsPreviewOpen(false); 
                handleCloseModal();      
                fetchDevicesSimData();   
            },
            (errorMsg) => {
                toast.error(errorMsg.message );
            }
        );
    };

    const handleServiceModelChange = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        if (value === 'ADD_NEW_MODEL') {
            setIsAddModelModalOpen(true);
        } else {
            setServiceModel(value);
        }
    };

    const handlePlatformModelChange = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        if (value === 'ADD_NEW_MODEL') {
            setIsAddPlatformModalOpen(true);
        } else {
            setPlatformModel(value);
        }
    };

    const handleAddNewModelSubmit = () => {
        if (newModelName.trim() !== '') {
            const newOption = { id: newModelName, label: newModelName };
            setServiceModelOptions(prevOptions => {
                const filtered = prevOptions.filter(opt => opt.id !== 'ADD_NEW_MODEL');
                return [...filtered, newOption, { id: 'ADD_NEW_MODEL', label: '+ Add New Model' }];
            });
            setServiceModel(newModelName);
            setIsAddModelModalOpen(false);
            setNewModelName('');
        }
    };

    const handleAddNewPlatformSubmit = () => {
        if (newPlatformName.trim() !== '') {
            const newOption = { id: newPlatformName, label: newPlatformName };
            setPlatformModelOptions(prevOptions => {
                const filtered = prevOptions.filter(opt => opt.id !== 'ADD_NEW_MODEL');
                return [...filtered, newOption, { id: 'ADD_NEW_MODEL', label: '+ Add New Model' }];
            });
            setPlatformModel(newPlatformName);
            setIsAddPlatformModalOpen(false);
            setNewPlatformName('');
        }
    };

    const handleTabCompanyChange = (event: SelectChangeEvent<string>) => {
        const companyId = event.target.value;
        setSelectedCompany(companyId);
        
        localStorage.setItem('selectedDeploymentCompany', companyId);
        
        setPage(0);
    };

    const handleModalCompanyChange = (event: SelectChangeEvent<string>) => {
        const companyId = event.target.value;
        setModalSelectedCompany(companyId);
        setSelectedVehicle('');
        setVehicleOptions([]);
        
        if (companyId) {
            fetchAvailableVehicles(companyId);
            fetchAssignedUsers(companyId);
        }
    };

    const handleSaveService = () => {
        if (!serviceType || !serviceModel || !sellingPrice || (serviceType === 'device' && !platformModel)) {
            console.error("Please fill in all required fields before saving.");
            return;
        }

        if (isEditMode && selectedServiceId) {
            editServices(
                selectedServiceId.toString(),
                serviceModel,
                serviceType,
                sellingPrice,
                platformModel,
                (response) => {
                    console.log("Service successfully updated!", response);
                    toast.success("Service updated successfully");
                    fetchServicesData(activeTab === 'Services'); 
                    handleCloseModal();  
                },
                (errorMsg) => {
                    console.error("Failed to update service: ", errorMsg);
                    toast.error("Failed to update service");
                }
            );
        } else {
            addServices(
                serviceType,
                serviceModel,
                sellingPrice,
                platformModel, 
                (response) => {
                    console.log("Service successfully added!", response);
                    fetchServicesData(activeTab === 'Services'); 
                    handleCloseModal();  
                },
                (errorMsg) => {
                    console.error("Failed to add service: ", errorMsg);
                }
            );
        }
    };
    
const handleSaveDeployment = (deviceId: number, simId: number) => {
    if (!modalSelectedCompany || !deploymentDevice || !selectedVehicle) {
        console.error("Please fill in all deployment fields.");
        toast.error("Please fill in all deployment fields.");
        return;
    }

    if (!deviceId || !simId) {
        console.error("Please select both a Device and a SIM.");
        toast.error("Please select both a Device and a SIM.");
        return;
    }

    const vehicleId = Number(selectedVehicle);
    const serviceId = Number(deploymentDevice);

    if (isNaN(vehicleId) || isNaN(serviceId)) {
         console.error("Invalid vehicle or service ID. They must be numeric.");
         return;
    }

    addDeviceSimPairing(deviceId, simId, (response) => {
        console.log(deviceId, simId, "Device-SIM pair added successfully!", response);
        
        addDeployment(
            deviceId,
            simId,
            vehicleId,
            serviceId,
            remarks,
            selectedAssignedUser,
            (response) => {
                console.log("Deployment successfully added!", response);
                handleCloseModal();
                fetchDeploymentData(); 
            },
            (errorMsg) => {
                console.error("Failed to add deployment: ", errorMsg);
                toast.error(errorMsg?.message || "Deployment failed. Rolling back statuses...");
                editDevicesSim(
                    'sim',
                    { sim_id: simId, sim_status: 'active' },
                    () => console.log("Rollback successful: SIM reverted to active"),
                    (err) => console.error("Rollback failed for SIM:", err)
                );
                editDevicesSim(
                    'device',
                    { device_id: deviceId, device_status: 'unallocated' },
                    () => console.log("Rollback successful: Device reverted to unallocated"),
                    (err) => console.error("Rollback failed for Device:", err)
                );
            }
        );
    }, (errorMsg) => {
        console.error("Failed to add Device-SIM pair: ", errorMsg);
        toast.error(errorMsg.message);
    });
};

    const renderModalContent = () => {
        switch (activeTab) {
            case 'Devices':
                return (
                    <AddDeviceModal 
                        deviceTypeOptions={deviceTypeOptions}
                        deviceType={deviceType}
                        setDeviceType={setDeviceType}
                        selectedFile={selectedFile}
                        handleFileChange={handleFileChange}
                    />
                );
            case 'SIM':
                return (
                    <AddSimModal 
                        serviceProviderOptions={serviceProviderOptions}
                        serviceProvider={serviceProvider}
                        setServiceProvider={setServiceProvider}
                        selectedFile={selectedFile}
                        handleFileChange={handleFileChange}
                    />
                );
            case 'Services':
                return (
                    <AddServicesModal 
                        serviceTypeOptions={serviceTypeOptions}
                        serviceType={serviceType}
                        setServiceType={setServiceType}
                        serviceModelOptions={serviceModelOptions}
                        serviceModel={serviceModel}
                        handleServiceModelChange={handleServiceModelChange}
                        platformModelOptions={platformModelOptions}
                        platformModel={platformModel}
                        handlePlatformModelChange={handlePlatformModelChange}
                        sellingPrice={sellingPrice}
                        setSellingPrice={setSellingPrice}
                        handleSaveService={handleSaveService}
                    />
                );
            case 'Deployment':
                return (
                    <AddDeploymentModal 
                        companyOptions={companyOptions} 
                        selectedCompany={modalSelectedCompany}
                        handleCompanyChange={handleModalCompanyChange}
                        platformModelOptions={platformModelOptions}
                        deploymentDevice={deploymentDevice}
                        setDeploymentDevice={setDeploymentDevice}
                        vehicleOptions={vehicleOptions}
                        selectedVehicle={selectedVehicle}
                        setSelectedVehicle={setSelectedVehicle}
                        remarks={remarks}
                        setRemarks={setRemarks}
                        handleSaveDeployment={handleSaveDeployment}
                        assignedUserOptions={assignedUserOptions}
                        selectedAssignedUser={selectedAssignedUser}
                        setSelectedAssignedUser={setSelectedAssignedUser}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <>
            <Header />
            <NavBar selected="Devices" />

            <div style={{ marginTop: "70px" }}>
                <Heading title="Devices" closeIcon="false" leftContent={
                    <Box display="flex" alignItems="center">
                        <Typography style={{ marginRight: '5px' }}>Show Entries:</Typography>
                        <Dropdown
                            options={showEntries}
                            selectedValue={pageSize.toString()}
                            handleChange={(event: SelectChangeEvent<string>) => updateField(event)}
                            width="90px"
                            height="50px"
                        />

                        {(activeTab !== 'Services') && (
                            <>
                                <Typography style={{ marginLeft: '15px', marginRight: '5px' }}>Status:</Typography>
                               <Dropdown
                                    options={
                                        activeTab === 'Devices' ? DEVICE_HEADER_STATUS_OPTIONS : 
                                        activeTab === 'SIM' ? SIM_HEADER_STATUS_OPTIONS : 
                                        DEPLOYMENT_HEADER_STATUS_OPTIONS
                                    }
                                    selectedValue={statusFilter}
                                    handleChange={handleStatusFilterChange}
                                    width="160px"
                                    height="50px"
                                />

                              
                            </>
                        )}

                       
                    </Box>
                } rightContent={
                    <>
                     {activeTab === 'Deployment' && (
                            <>
                                <Typography style={{ marginLeft: '15px', marginRight: '5px' }}>Company:</Typography>
                                <Dropdown
                                    options={[{ id: 'ALL', label: 'All' }, ...companyOptions]}
                                    selectedValue={selectedCompany}
                                    handleChange={handleTabCompanyChange}
                                    width="200px"
                                    height="50px"
                                    searchable={true}
                                />
                            </>
                        )}
                    {activeTab === 'SIM' && statusFilter === 'inactive' && selectedRows.length > 0 && (
            <>
                <Dropdown
                    options={[
                        { id: 'default', label: 'Select Action' },
                        { id: 'active', label: 'Make Active' },
                        { id: 'returned', label: 'Make Returned' }
                    ]}
                    selectedValue="default"
                    handleChange={initiateBulkStatusUpdate}
                    width="160px"
                    height="50px"
                />
            </>
        )}
                    {(activeTab === 'Devices' || activeTab === 'SIM') && (
                            <>
                              
                                <TextField
                                    size="small"
                                    placeholder={`Search ${activeTab}...`}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSearchClick();
                                        }
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={handleSearchClick} edge="end">
                                                    <SearchIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                    sx={{ 
                                        marginLeft: '15px', 
                                        width: '200px', 
                                        backgroundColor: 'white', 
                                        borderRadius: '4px' 
                                    }}
                                />
                            </>
                        )}

                    <Button className="custom-button" onClick={handleOpenModal}>
                        {`Add ${activeTab}`}
                    </Button>
                    
                        
                    </>
                } />
                
                <Box sx={{ marginBottom: 2, marginLeft: { xs: '10px', sm: '180px' } }}>
                     <StatusFilterTabs 
                        tabs={isMobile ? ['Deployment'] : ['Devices', 'SIM', 'Services' , 'Deployment']}
                        activeTab={activeTab} 
                        onTabChange={handleTabChange} 
                    />
                </Box>

                <Box className="table-container custom-data-grid">                
                   <DataGrid
                        rows={rows}
                        columns={columns}
                        loading={loading}
                        hideFooterPagination
                        disableColumnResize
                        disableRowSelectionOnClick
                        autoHeight={false}
                        rowCount={totalRecords}
                        classes={{ columnHeader: 'red-header' }}
                        checkboxSelection={activeTab === 'SIM' && statusFilter === 'inactive'}
                        onRowSelectionModelChange={(newSelectionModel) => setSelectedRows(newSelectionModel)}
                        rowSelectionModel={selectedRows}
                    />
                </Box>
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px', marginLeft: '180px' }}>
                    <Newpagination rows={totalRecords} pageSize={pageSize} page={page} handlePageChange={handlePageClick} />
                </div>
                
            </div>

         <Dialog open={isModalOpen} onClose={handleCloseModal} PaperProps={{ sx: { borderRadius: '8px', width: { xs: '90%', sm: '400px' } } }}>
                <Box sx={{ marginLeft: { xs: '0px', sm: '-180px' }}}>
                    <Heading title={isEditMode ? `Edit ${activeTab}` : `Add ${activeTab}`} closeIcon="true" onClose={handleCloseModal} /> 
                </Box>
                <DialogContent sx={{ minHeight: '150px' }}>
                    {renderModalContent()}
                </DialogContent>
            </Dialog>

           <Dialog 
    open={isFaultyModalOpen} 
    onClose={() => setIsFaultyModalOpen(false)}
    maxWidth={false}
    PaperProps={{ sx: { width: '350px' } }}
>
    <Box sx={{ marginLeft: { xs: '0px', sm: '-180px' }}}>
        <Heading  
            title='Select Faulty Component' 
            closeIcon='true' 
            onClose={() => setIsFaultyModalOpen(false)} 
        />
    </Box>
    <DialogContent>
        <Box sx={{ display:'flex', justifyContent:'center' }}>
        <Dropdown
                    options={[
                        { id: 'sim', label: 'SIM' },
                        { id: 'device', label: 'Device' }
                    ]}
                    selectedValue={faultyComponentSelection}
                    handleChange={(e: SelectChangeEvent<string>) => setFaultyComponentSelection(e.target.value)}
                    placeholder='Select Faulty Component'
                    label='Select Faulty Component'
                    width="240px"
                    height="45px"
                />
                </Box>
    </DialogContent>
    <DialogActions sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                    <Button 
                        variant="outlined" 
                        className='custom-cancel-button'                       
                        onClick={() => setIsFaultyModalOpen(false)}
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="contained" 
                        className='custom-button'
                        onClick={handleFaultyComponentSubmit}
                    >
                        Confirm Faulty
                    </Button>
                </DialogActions>
</Dialog>
            
         <ConfirmationPopup 
            open={isStatusConfirmOpen} 
            onClose={() => { 
                setIsStatusConfirmOpen(false); 
                setPendingStatusChange(null); 
            }} 
            onConfirm={executeStatusUpdate} 
            title={`Confirm Status Change`} 
            message={
                pendingStatusChange?.isBulk 
                ? `Are you sure you want to change the status of the ${pendingStatusChange.selectedIds?.length} selected SIMs to '${pendingStatusChange.newStatus}'?` 
                : `Are you sure you want to change the status to '${pendingStatusChange?.newStatus}'?`
            } 
        />

        <Dialog 
            open={isPhotoModalOpen} 
            onClose={handleClosePhotoModal}
            PaperProps={{ sx: { borderRadius: '8px', width: { xs: '90%', sm: '400px' } } }}
        >
            <Box sx={{ marginLeft: { xs: '0px', sm: '-180px' }}}>
                <Heading title="Upload Deployment Photos" closeIcon="true" onClose={handleClosePhotoModal} />
            </Box>
            <DialogContent>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Vehicle Number Plate Photo *</Typography>
                    <input 
                        type="file" 
                        accept="image/*" 
                        capture="environment" 
                        onChange={(e) => handleImageSelection(e.target.files?.[0], setVehicleNumberPlatePic)} 
                    />
                </Box>
                <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Deployed Device Photo *</Typography>
                    <input 
                        type="file" 
                        accept="image/*" 
                        capture="environment" 
                        onChange={(e) => handleImageSelection(e.target.files?.[0], setDeployedDevicePic)} 
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ display: 'flex', justifyContent: 'center', gap: 2, pb: 3 }}>
                <Button variant="outlined" className='custom-cancel-button' onClick={handleClosePhotoModal}>
                    Cancel
                </Button>
                <Button variant="contained" className='custom-button' onClick={handlePhotoUploadAndDeploy}>
                    Submit & Deploy
                </Button>
            </DialogActions>
        </Dialog>

            <AddNewModelModal open={isAddModelModalOpen} title="Add New Model" value={newModelName} onChange={setNewModelName} onClose={() => setIsAddModelModalOpen(false)} onSubmit={handleAddNewModelSubmit} />
            <AddNewModelModal open={isAddPlatformModalOpen} title="Add New Platform" value={newPlatformName} onChange={setNewPlatformName} onClose={() => setIsAddPlatformModalOpen(false)} onSubmit={handleAddNewPlatformSubmit} />
            <PreviewFileModal open={isPreviewOpen} onClose={() => setIsPreviewOpen(false)} onConfirm={confirmAndUpload} fileName={selectedFile?.name} previewData={previewData} previewColumns={previewColumns} />
        </>
    );
};

export default Devices;